import React from "react";
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="mb-12 space-y-2">
          <h1 className="text-4xl font-bold tracking-tight">Privacy Policy</h1>
          <p className="text-muted-foreground">
            Company: To Better Digital (TBD)
          </p>
          <p className="text-muted-foreground">
            Website:{" "}
            <a href="https://to-better-digital.lovable.app" className="text-primary hover:underline">
              https://to-better-digital.lovable.app
            </a>
          </p>
          <p className="text-muted-foreground">
            Contact:{" "}
            <a href="mailto:jeremy@tobetterdigital.com" className="text-primary hover:underline">
              jeremy@tobetterdigital.com
            </a>
          </p>
          <p className="text-sm text-muted-foreground">
            Last updated: March 3, 2026
          </p>
        </div>

        <div className="space-y-10 text-sm leading-relaxed text-muted-foreground">
          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">1. Overview</h2>
            <p className="mb-3">
              This Privacy Policy explains how To Better Digital (TBD) ("we", "us", "our") collects, uses, discloses, and protects personal data when you use our advertising automation platform (the "Service"). The Service enables users to connect their Meta (Facebook/Instagram) advertising accounts and, optionally, Google Drive to bulk-create and manage advertising campaigns and related assets.
            </p>
            <p>
              We primarily serve users in the European Economic Area (EEA), the United Kingdom, and the United States. Where applicable, we comply with the EU General Data Protection Regulation ("GDPR") and other relevant privacy laws.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">2. Data Controller</h2>
            <p className="mb-3">
              To Better Digital (TBD) is the data controller for personal data processed in connection with your use of the Service, except where we act as a processor on behalf of a business customer (for example, where an agency uses the Service to operate advertising on behalf of its own clients).
            </p>
            <p>
              If you are using the Service under a business or agency account, your organization may also be a controller for certain data. If you have questions about your organization's handling of your data, please contact your organization directly.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">3. Personal Data We Collect</h2>

            <h3 className="mb-2 font-medium text-foreground">3.1 Account and profile data</h3>
            <ul className="mb-4 list-disc space-y-1 pl-6">
              <li>Email address and authentication credentials (via Supabase Auth).</li>
              <li>Basic account details you provide (e.g., name or workspace name, if entered).</li>
              <li>Support communications (messages and attachments you send to us).</li>
            </ul>

            <h3 className="mb-2 font-medium text-foreground">3.2 Meta (Facebook/Instagram) connected account data</h3>
            <p className="mb-2">
              When you connect Meta via OAuth ("Facebook Login for Business"), we receive and process data needed to provide the Service, including:
            </p>
            <ul className="mb-2 list-disc space-y-1 pl-6">
              <li>Meta access token (stored encrypted in our database).</li>
              <li>Meta ad account and business identifiers you authorize us to access.</li>
              <li>Campaign/ad set/ad metadata and performance metrics that the Service syncs or displays (e.g., names, IDs, statuses, spend, impressions, clicks).</li>
              <li>Pages and Instagram account lists, where relevant to ad configuration.</li>
            </ul>
            <p className="mb-4">
              The Meta permissions (scopes) requested are: ads_management, ads_read, business_management, pages_show_list, pages_manage_ads.
            </p>

            <h3 className="mb-2 font-medium text-foreground">3.3 Google Drive connected account data (optional)</h3>
            <p className="mb-2">
              If you optionally connect Google Drive via OAuth, we request the drive.readonly scope. This allows us to read files you choose to use as creative assets. We do not request permissions to modify, delete, or write files.
            </p>
            <ul className="mb-2 list-disc space-y-1 pl-6">
              <li>Google refresh token (stored encrypted in our database).</li>
              <li>File metadata necessary to locate and stream the file (e.g., file ID, name, mime type), as selected by you.</li>
            </ul>
            <p className="mb-4">
              Important: Files are streamed from Google Drive directly to Meta's servers for upload where possible. We do not store your Google Drive files on our infrastructure.
            </p>

            <h3 className="mb-2 font-medium text-foreground">3.4 Service usage and technical data</h3>
            <ul className="mb-4 list-disc space-y-1 pl-6">
              <li>Log data (e.g., timestamps, request identifiers, error logs, and security/audit logs).</li>
              <li>Device and connection data (e.g., IP address, user agent) as typically generated by browsers/servers.</li>
              <li>Session and authentication tokens (JWT) used to keep you logged in.</li>
            </ul>

            <h3 className="mb-2 font-medium text-foreground">3.5 Cookies</h3>
            <p>
              We use only essential cookies or similar storage necessary to provide the Service (for example, session cookies). We do not use third-party advertising cookies or trackers on the platform.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">4. How We Use Personal Data</h2>
            <p className="mb-2">We use personal data to:</p>
            <ul className="mb-3 list-disc space-y-1 pl-6">
              <li>Provide, operate, and maintain the Service (including authentication and account administration).</li>
              <li>Connect to Meta and Google Drive APIs at your direction to perform requested actions (e.g., creating campaigns, uploading assets, syncing insights).</li>
              <li>Store and display your configurations, templates, and advertising data within your workspace.</li>
              <li>Ensure security, prevent abuse, detect fraud, and troubleshoot issues.</li>
              <li>Communicate with you about the Service (support responses, service announcements, policy updates).</li>
              <li>Comply with legal obligations and enforce our terms.</li>
            </ul>
            <p>
              We do not sell your data. We do not use your data for our own advertising. We process data only to provide and improve the Service.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">5. Google API Services User Data Policy Compliance</h2>
            <p>
              To Better Digital's use and transfer to any other app of information received from Google APIs will adhere to the{" "}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements. We strictly limit our access to reading files you explicitly select for the sole purpose of uploading them to your Meta advertising campaigns.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">6. Legal Bases for Processing (GDPR)</h2>
            <p className="mb-2">If you are in the EEA/UK, our legal bases include:</p>
            <ul className="list-disc space-y-1 pl-6">
              <li><strong className="text-foreground">Contract:</strong> processing necessary to provide the Service you request (e.g., account access, running syncs, creating campaigns).</li>
              <li><strong className="text-foreground">Legitimate interests:</strong> security, fraud prevention, service reliability, and basic internal analytics limited to what is necessary to operate the Service (balanced against your rights).</li>
              <li><strong className="text-foreground">Consent:</strong> where required by law (e.g., if we were to introduce non-essential cookies; currently we use only essential cookies).</li>
              <li><strong className="text-foreground">Legal obligation:</strong> compliance with applicable laws, lawful requests, and record-keeping requirements.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">7. Where Data Is Stored and Processed</h2>
            <ul className="mb-3 list-disc space-y-1 pl-6">
              <li><strong className="text-foreground">Database and authentication:</strong> Supabase (PostgreSQL + Supabase Auth). OAuth tokens are stored encrypted.</li>
              <li><strong className="text-foreground">Workflow automation:</strong> self-hosted n8n instance used for backend processing and API calls.</li>
              <li><strong className="text-foreground">Hosting:</strong> a European VPS (Hostinger, data center in Europe) used to run our backend services.</li>
              <li><strong className="text-foreground">Third-party APIs:</strong> Meta and Google APIs are accessed at your request to perform Service functions.</li>
            </ul>
            <p>
              Because Meta and Google operate globally, personal data may be processed outside your country of residence, including in the United States. Where GDPR applies and data is transferred outside the EEA/UK, we rely on appropriate safeguards (such as Standard Contractual Clauses) where applicable.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">8. Sharing and Disclosure</h2>
            <p className="mb-2">We share personal data only as needed to provide the Service, including:</p>
            <ul className="mb-3 list-disc space-y-1 pl-6">
              <li><strong className="text-foreground">Meta (Facebook/Instagram):</strong> to create/manage campaigns and upload creative assets to your Meta ad account, as instructed by you.</li>
              <li><strong className="text-foreground">Google (Google Drive):</strong> to read and stream files you select, as instructed by you.</li>
              <li><strong className="text-foreground">Supabase:</strong> hosting and database services for account data and encrypted tokens.</li>
              <li><strong className="text-foreground">Hostinger / infrastructure providers:</strong> hosting for our backend services in Europe.</li>
              <li><strong className="text-foreground">Service providers:</strong> vendors that help us operate the Service (e.g., email delivery for transactional messages), if used.</li>
              <li><strong className="text-foreground">Legal and safety:</strong> where required by law or to protect rights, safety, and security.</li>
            </ul>
            <p>
              We do not permit third parties to use your data for their own marketing purposes based solely on our sharing. Meta and Google may process data under their own terms and policies when you use their services.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">9. Data Retention</h2>
            <p className="mb-2">
              We retain personal data only as long as necessary to provide the Service, meet legal obligations, resolve disputes, and enforce agreements.
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li><strong className="text-foreground">Account data:</strong> retained while your account is active and for a limited period after closure as required for legal, security, or backup purposes.</li>
              <li><strong className="text-foreground">OAuth tokens:</strong> stored encrypted and retained while the connection is active; removed or invalidated when you disconnect the integration or delete your account.</li>
              <li><strong className="text-foreground">Cached Meta data and insights:</strong> retained to provide reporting and continuity; you may request deletion as described below.</li>
              <li><strong className="text-foreground">Logs:</strong> retained for security and debugging for a limited period, then deleted or anonymized where feasible.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">10. Security</h2>
            <p className="mb-3">
              We use administrative, technical, and organizational measures designed to protect personal data, including encryption of OAuth tokens at rest, tenant isolation using Row Level Security (RLS), and access controls. However, no system can be guaranteed 100% secure.
            </p>
            <p>
              You are responsible for keeping your login credentials secure and for maintaining appropriate security on your Meta and Google accounts.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">11. Your Rights (EEA/UK)</h2>
            <p className="mb-2">If GDPR applies to you, you may have the right to:</p>
            <ul className="mb-3 list-disc space-y-1 pl-6">
              <li>Access your personal data and obtain a copy.</li>
              <li>Rectify inaccurate or incomplete data.</li>
              <li>Request deletion (erasure) of your data.</li>
              <li>Restrict processing in certain circumstances.</li>
              <li>Object to processing based on legitimate interests.</li>
              <li>Data portability for data you provided to us, where applicable.</li>
              <li>Withdraw consent where processing is based on consent.</li>
            </ul>
            <p className="mb-3">
              To exercise these rights, contact us at{" "}
              <a href="mailto:jeremy@tobetterdigital.com" className="text-primary hover:underline">
                jeremy@tobetterdigital.com
              </a>
              . We may ask you to verify your identity before responding.
            </p>
            <p>
              You also have the right to lodge a complaint with your local data protection authority. If you are in France, you may contact the CNIL.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">12. US Privacy Notice</h2>
            <p>
              If you are in the United States, you may have additional rights depending on your state (e.g., access, deletion, and correction). We do not sell personal information and do not share it for cross-context behavioral advertising as defined by certain US state privacy laws. You may contact us to exercise applicable rights.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">13. How to Revoke Access</h2>
            <p className="mb-2">You can revoke TBD's access to your connected accounts at any time:</p>
            <ul className="list-disc space-y-1 pl-6">
              <li>
                <strong className="text-foreground">Meta:</strong> Go to{" "}
                <a href="https://www.facebook.com/settings?tab=business_tools" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Facebook Settings → Business Integrations
                </a>{" "}
                and remove To Better Digital.
              </li>
              <li>
                <strong className="text-foreground">Google Drive:</strong> Go to{" "}
                <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Google Account → Security → Third-party apps
                </a>{" "}
                and revoke access for To Better Digital.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">14. Children</h2>
            <p>
              The Service is not intended for children and may only be used by individuals who can form a legally binding contract in their jurisdiction. We do not knowingly collect personal data from children.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">15. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will post the updated version on this page and revise the "Last updated" date above. If changes are material, we may provide additional notice in the Service.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">16. Contact</h2>
            <p>
              If you have questions about this Privacy Policy or our privacy practices, contact:{" "}
              <a href="mailto:jeremy@tobetterdigital.com" className="text-primary hover:underline">
                jeremy@tobetterdigital.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}